export type LegacyMoneyAmount = {
  amount?: string;
  currency?: string;
};

export type MoneyAmount = {
  amount: number;
  currency: string;
};

/** Stored cents (number) or legacy major units (string). */
export type StoredMoneyAmount = MoneyAmount | LegacyMoneyAmount;

/**
 * Converts a stored money amount to major currency units for display.
 * SelectCurrency stores cents (number, or integer string when JSON typing is lost).
 * LegacySelectCurrency stores major units as a string (often with decimals).
 */
export function moneyAmountToMajorUnits(
  amount: number | string | undefined,
): number | undefined {
  if (amount == null || amount === "") {
    return undefined;
  }
  if (typeof amount === "number") {
    return Number.isFinite(amount) ? amount / 100 : undefined;
  }
  const raw = String(amount).trim();
  const parsed = parseFloat(raw.replace(",", "."));
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  // Legacy major units may use decimal separators (e.g. "20000.50").
  if (/[.,]/.test(raw)) {
    return parsed;
  }
  // Integer strings: cent amounts (e.g. "2000000") vs legacy major units (e.g. "20000").
  if (/^\d+$/.test(raw)) {
    if (parsed >= 100_000) {
      return parsed / 100;
    }
    return parsed;
  }
  return parsed;
}

/** Ensures SelectCurrency fields use `{ amount: number, currency }` (cents). */
export function normalizeMoneyAmount(value: unknown): MoneyAmount | undefined {
  if (value == null || value === "") {
    return undefined;
  }
  const parsed =
    typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined;
  if (!parsed) {
    return undefined;
  }
  const rawAmount = parsed.amount;
  const currency =
    typeof parsed.currency === "string" && parsed.currency.trim() !== ""
      ? parsed.currency
      : "EUR";
  if (rawAmount == null || rawAmount === "") {
    return undefined;
  }
  const amount =
    typeof rawAmount === "number" ? rawAmount : Number(String(rawAmount).trim());
  if (!Number.isFinite(amount)) {
    return undefined;
  }
  return { amount, currency };
}

export interface DimensionsDTO {
  cm_width?: number;
  in_width?: string;
  cm_height?: number;
  in_height?: string;
  cm_depth?: number;
  in_depth?: string;
  cm_volume?: number;
  in_volume?: string;
  cm_surface?: number;
  in_surface?: string;
  cm_diameter?: number;
  in_diameter?: string;
}
